import './globals.css'
import { match, P } from 'ts-pattern'
import { DBProvider } from '@/lib/db-context'
import schemaContent from '@/schemas/main.sql?raw'
import { useAllRoutes } from './router'
import { useFocusReset, useLocation } from '@swan-io/chicane'
import { AppShell } from '@/components/AppShell'
import { useUser } from '@/hooks/user'
import { useCustomer, fetchCustomer } from '@/stores/customer'
import { useFeatures } from '@/stores/features'
import { Suspense, lazy, useMemo, useEffect, useRef } from 'react'
import { NotFound } from '@/pages/NotFound'

const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Auth = lazy(() => import('@/pages/Auth'))
const User = lazy(() => import('@/pages/User'))
const ProgramIndex = lazy(() => import('@/pages/ProgramIndex'))
const ProgramView = lazy(() => import('@/pages/ProgramView'))
const ProgramWorkoutView = lazy(() => import('@/pages/ProgramWorkoutView'))
const WorkoutExerciseView = lazy(() => import('@/pages/WorkoutExerciseView'))
const ExerciseIndex = lazy(() => import('@/pages/ExerciseIndex'))
const ExerciseView = lazy(() => import('@/pages/ExerciseView'))
const ProgressionIndex = lazy(() => import('@/pages/ProgressionIndex'))
const ProgressionView = lazy(() => import('@/pages/ProgressionView'))
const ActivityIndex = lazy(() => import('@/pages/ActivityIndex'))
const ActivityView = lazy(() => import('@/pages/ActivityView'))
const Stats = lazy(() => import('@/pages/Stats'))
const CompletedSetsIndex = lazy(() => import('@/pages/CompletedSets'))
const Quickadd = lazy(() => import('@/pages/Quickadd'))

const CustomerFeatures = lazy(() => import('@/components/CustomerFeatures'))

export default function App() {
  const profileId = useUser((c) => c.profileId)
  const userId = useUser((c) => c.userId)
  const content = useMemo(() => schemaContent, [])
  const features = useFeatures()

  useEffect(() => {
    if (!profileId || !userId) return

    Promise.all([fetchCustomer(), features.fetchConfig(profileId)])
  }, [profileId, userId])

  if (!profileId) {
    return (
      <div className="w-full h-full absolute flex flex-col justify-center items-center">
        <div className="text-emerald-400 text-3xl font-bold monospace">
          Starting
        </div>
        <div className="absolute w-full h-full bg-gradient-to-r to-emerald-300 from-stone-300 animate-pulse duration-4000"></div>
        <div className="absolute w-full h-full bg-gradient-to-t from-emerald-emerald-300 to-amber-900 animate-pulse duration-10000"></div>
      </div>
    )
  }

  return (
    <AppShell>
      <DBProvider
        dbname={profileId as string}
        schema={{
          name: 'main.sql',
          content,
        }}
        Render={() => (
          <Suspense>
            <Routes />
          </Suspense>
        )}
      />
    </AppShell>
  )
}

function Routes() {
  const route = useAllRoutes()
  const location = useLocation()
  const isCustomer = useCustomer((c) => c.status > 0)
  const containerRef = useRef(null)

  useFocusReset({ route, containerRef })

  return (
    <div className="contents" ref={containerRef}>
      {match(route)
        .with({ name: 'Auth' }, () => <Auth />)
        .with({ name: 'User' }, () => <User />)
        .with({ name: 'ProgramIndex' }, () => <ProgramIndex />)
        .with(
          { name: 'ProgramView', params: { id: P.string.select() } },
          (id) => <ProgramView id={id} />
        )
        .with(
          { name: 'ProgramWorkoutView', params: { id: P.string.select() } },
          (id) => <ProgramWorkoutView id={id} />
        )
        .with(
          { name: 'WorkoutExerciseView', params: { id: P.string.select() } },
          (id) => <WorkoutExerciseView id={id} />
        )
        .with({ name: 'ExerciseIndex' }, () => <ExerciseIndex />)
        .with(
          { name: 'ExerciseView', params: { id: P.string.select() } },
          (id) => <ExerciseView id={id} />
        )
        .with({ name: 'ProgressionIndex' }, () => <ProgressionIndex />)
        .with(
          { name: 'ProgressionView', params: { id: P.string.select() } },
          (id) => <ProgressionView id={id} />
        )
        .with({ name: 'ActivityIndex' }, () => <ActivityIndex />)
        .with(
          { name: 'ActivityView', params: { id: P.string.select() } },
          (id) => <ActivityView id={id} />
        )
        .with(
          { name: 'StatsIndex' },
          { name: 'StatsMax' },
          { name: 'StatsVolume' },
          () => <Stats />
        )
        .with({ name: 'CompletedSetsIndex' }, () => <CompletedSetsIndex />)
        .with({ name: 'Quickadd' }, () => <Quickadd />)
        .with(
          { name: 'Root', params: { date: P.optional(P.string) } },
          (opts) => <Dashboard date={opts.params.date} />
        )
        .otherwise(() => {
          const [base, path] = location.path
          // Shitty router will not match without trailing slashes
          if (base === 'app' && path === '') {
            return <Dashboard />
          }
          return <NotFound />
        })}
      {isCustomer && <CustomerFeatures />}
    </div>
  )
}
